import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

export default function LeadCapture({ data }) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: values.name || null,
        phone: values.phone || null,
        email: values.email || null,
        source: "landing-form",
      };
      await api.post("/leads", payload);
      setSuccess(true);
      setValues({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="lead-capture-section" className="py-24 lg:py-32 bg-alabaster">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-white border border-border p-8 lg:p-14 grid md:grid-cols-2 gap-10 items-center"
        >
          <div>
            <p className="overline mb-4" data-testid="lead-capture-overline">{data.overline}</p>
            <h2 data-testid="lead-capture-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-onyx leading-tight">
              {data.title}
            </h2>
            <p className="mt-4 text-onyx/70">{data.subtitle}</p>
            <p className="mt-6 text-xs text-onyx/40">{data.privacyNote}</p>
          </div>

          {success ? (
            <div data-testid="lead-capture-success" className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold mb-5">
                <Check size={28} className="text-onyx" strokeWidth={2} />
              </div>
              <p className="font-serif text-2xl text-onyx">{data.successMessage}</p>
            </div>
          ) : (
            <form data-testid="lead-capture-form" onSubmit={onSubmit} className="space-y-4">
              {(data.fields || []).map((f, i) => (
                <div key={f.key}>
                  <label className="block text-xs tracking-[0.2em] uppercase text-onyx/60 mb-2">
                    {f.label} {f.required && <span className="text-gold">*</span>}
                  </label>
                  <input
                    data-testid={`lead-field-${f.key}`}
                    type={f.key === "email" ? "email" : f.key === "phone" ? "tel" : "text"}
                    required={f.required}
                    value={values[f.key] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    className="w-full px-3 py-3 border border-border bg-alabaster text-onyx focus:outline-none focus:border-gold transition-colors text-sm"
                  />
                </div>
              ))}
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-capture-submit"
                className="group w-full inline-flex items-center justify-center gap-3 py-4 bg-onyx text-alabaster text-xs tracking-[0.25em] uppercase font-semibold hover:bg-gold hover:text-onyx transition-colors disabled:opacity-50"
              >
                {submitting ? "Enviando…" : data.submitLabel}
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
